import { DeliveryWindowOutputRecord } from '@navio/nsdk';
import { cn } from 'noddi-ui-common';
import { formatCurrencyAmount, slotCapacityFromEmbeddedSupply } from 'noddi-util';
import { useAuthContext } from '../../contexts/useAuthContext';
import { useShowSlotAvailability } from '../../stores/DevSettingsStore';
import { hasSlotCapacityToShow } from '../Capacity/slotCapacityInfo';
import { SlotCapacityInline } from '../Capacity/SlotCapacityInline';
import { SlotCapacityTooltip } from '../Capacity/SlotCapacityTooltip';
import {
  isSameDeliveryWindow,
  isTimeWindowUnavailable,
  parseTimeSlotFromPublicTimes,
  UNAVAILABLE_TEXT,
  UNAVAILABLE_TEXT_ON_PAGE
} from './utils';

type Props = {
  timeWindow: DeliveryWindowOutputRecord;
  translations: {
    soldOut: string;
  };
  onAvailableSelect: (bookingTimeWindow: DeliveryWindowOutputRecord) => void;
  onUnavailableSelect?: (bookingTimeWindow: DeliveryWindowOutputRecord) => void;
  selectedDeliveryWindow?: { startsAt: string; endsAt: string };
  /** Day the slot belongs to. The clock time alone does not tell two days apart. */
  dateLabel?: string;
  /** `inline` puts the price at the row's end (one day per screen); `stacked` puts it under the time (day columns). */
  layout: 'inline' | 'stacked';
  /** The row sits straight on the page background rather than on the white card. */
  isOnPage: boolean;
};

export const TimeWindowRow = ({
  timeWindow,
  onAvailableSelect,
  onUnavailableSelect,
  translations,
  selectedDeliveryWindow,
  dateLabel,
  layout,
  isOnPage
}: Props) => {
  const { isSuperUser } = useAuthContext();
  const showSlotAvailability = useShowSlotAvailability();

  const isUnavailable = isTimeWindowUnavailable({ timeWindow });
  // Sold-out slots stay focusable so the reason can be heard. Superusers can still select them.
  const cannotSelect = isUnavailable && !isSuperUser;
  const isSelected = isSameDeliveryWindow(timeWindow, selectedDeliveryWindow);

  const { startsAt, endsAt, timeZone, deliveryWindowFee } = timeWindow;
  const capacity = {
    ...slotCapacityFromEmbeddedSupply({
      contributionCount: timeWindow.contributionCount,
      baselineContributionCount: timeWindow.baselineContributionCount,
      contributions: timeWindow.contributions
    }),
    totalRemainingSeconds: timeWindow.totalRemainingSeconds
  };
  const showCapacity = showSlotAvailability && hasSlotCapacityToShow(capacity);

  const priceText = isUnavailable
    ? translations.soldOut
    : formatCurrencyAmount({ money: deliveryWindowFee, decimals: 0 });
  const formattedSlot = parseTimeSlotFromPublicTimes({ startsAt, endsAt, timeZone });
  const accessibleName = [dateLabel, formattedSlot, priceText].filter(Boolean).join(', ');

  const handleOnClick = () => {
    if (cannotSelect) {
      return;
    }
    if (isUnavailable) {
      onUnavailableSelect?.(timeWindow);
    } else {
      onAvailableSelect(timeWindow);
    }
  };

  return (
    <SlotCapacityTooltip capacity={capacity} enabled={showCapacity}>
      <button
        type='button'
        onClick={handleOnClick}
        aria-disabled={cannotSelect || undefined}
        aria-label={accessibleName}
        aria-pressed={isSelected}
        className={cn(
          // `py-1` keeps both kinds of row inside the 56px minimum, so a time sits level across day columns.
          'flex w-full shrink-0 rounded-lg border py-1 transition-colors',
          // Opaque brand ring, tight to the box: a gray ring offset from a filled box read as a second border.
          'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-button-primary',
          layout === 'inline'
            ? 'items-center justify-between gap-3 px-4 text-left'
            : 'mx-auto max-w-32 flex-col items-center justify-center px-2',
          // Only a bookable window is a box; sold out stays plain text, so the boxes read as the choices.
          isSelected
            ? 'border-card-selected-background bg-card-selected-background text-card-selected-foreground'
            : isUnavailable
              ? cn('border-transparent', isOnPage ? UNAVAILABLE_TEXT_ON_PAGE : UNAVAILABLE_TEXT)
              : 'border-input bg-background text-foreground shadow-xs hover:border-primary/60',
          isUnavailable && layout === 'inline' ? 'min-h-11' : 'min-h-14',
          isUnavailable && !isSelected && (cannotSelect ? 'cursor-not-allowed' : 'hover:bg-accent')
        )}
      >
        {/* Capacity (superuser dev setting) sits under the time, so the price keeps its place. */}
        <span className={cn('flex flex-col', layout === 'inline' ? 'items-start' : 'items-center')}>
          <span className={cn('text-sm whitespace-nowrap', isUnavailable ? 'font-normal' : 'font-medium')}>
            {formattedSlot}
          </span>
          {showCapacity && (
            <SlotCapacityInline
              capacity={capacity}
              isSelected={isSelected}
              className={cn(layout === 'inline' && 'items-start')}
            />
          )}
        </span>
        <span className={cn('text-xs whitespace-nowrap', !isSelected && !isUnavailable && 'text-muted-foreground')}>
          {priceText}
        </span>
      </button>
    </SlotCapacityTooltip>
  );
};
